import React, {createContext, useCallback, useContext, useEffect, useMemo, useReducer} from 'react';
import {useQuery} from 'react-query';
import {actions, initialState} from './actions';
import {reducer} from './NewRR.reducer';
import {getGetters} from './getters';
import {getToggles} from './toggles';
import {getCustomFilterKeys} from '../FiltersMapper';
import {searchPatientReferrals} from '../../../../actions/patientSearch/patientSearchActions';
import {exportToCSV} from './api';
import {defaultSortParams} from '../const';
import {useLocation} from "react-router-dom";
import {TRACK_EVENTS, trackPendoEvent} from "../../../../utils/pendoTrack";

const NewRRContext = createContext();
const EVENT_ACTION_TO_PENDO_EVENT = {
  CLICK_SAVE_WORKLIST: TRACK_EVENTS.QUICKLIST_CUSTOM_WORKLIST_SAVE,
  DELETE_WORKLIST: TRACK_EVENTS.QUICKLIST_CUSTOM_WORKLIST_DELETE
};

const NewRRProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const location = useLocation();

  const getters = getGetters(state);
  const toggles = useMemo(() => getToggles(dispatch), [dispatch]);

  const sendEvent = async (action) => {
    const eventName = EVENT_ACTION_TO_PENDO_EVENT[action];
    if (!eventName) return;
    trackPendoEvent(eventName, {
      route: location?.pathname || window?.location?.pathname || "/",
      quicklist_type: "NEW_REFERRALS_RECEIVED",
      source_module: "NewRR.context",
      action_type: action
    });
  }

  const { refetch: getReferrals } = useQuery({
    queryKey: ['referralsSearch'],
    queryFn: () => {

      dispatch({ type: actions.SET_LOADING, payload: true })
      dispatch({ type: actions.SET_REFERRALS_SELECTED, payload: [] })

      let payload = {
        filterParams: [],
        pageNumber: 1,
        pageSize: 20,
        sortParams: defaultSortParams,
        previousTotalSize: 0,
        ...state.referralsSearch,
        quickListName: "REFERRALS_RECEIVED"
      };

      payload.filterParams = getCustomFilterKeys(payload.filterParams);

      toggles.setReferralsSearch({ ...payload })

      return searchPatientReferrals(payload).then(res => {
        dispatch({type: actions.SET_REFERRALS, payload: res?.data})
        dispatch({type: actions.SET_LOADING, payload: false})
      })
      .catch(err => dispatch({type: actions.SET_LOADING, payload: false}))
    },
    refetchOnWindowFocus: false,
    enabled: false,
    staleTime: 10000
  })

  const referralsSearchTimestamp = state?.referralsSearch?.timestamp;

  const getReferralsAsync = useCallback(async () => {
    getReferrals()
  }, [getReferrals]);

  useEffect(() => {

    if (referralsSearchTimestamp) {
      getReferralsAsync()
    }
  }, [
    getReferralsAsync,
    referralsSearchTimestamp
  ])

  const callbacks = {
    getReferrals: () => getReferralsAsync(),
    setReferralsSelected: (params) => {
      let index = state.referralsSelected.map(r => r[1]).indexOf(params[1])
      let crr = [...state.referralsSelected]
      let payload
      if (index >= 0) {
        crr.splice(index, 1)
        payload = [...crr]
      } else {
        payload = [...state.referralsSelected, params]
      }
      dispatch({ type: actions.SET_REFERRALS_SELECTED, payload: payload })
    },
    selectAll: (params) => {
      dispatch({ type: actions.SET_REFERRALS_SELECTED, payload: params })
    },
    clearSelected: () => {
      dispatch({ type: actions.SET_REFERRALS_SELECTED, payload: [] })
    },
    exportToCSV: async () => {
      const parsedReferrals = state.referralsSelected.map(r => r[1])
      const res = await exportToCSV({
        selectedItems: parsedReferrals,
        quickList: "REFERRALS_RECEIVED",
        patientRequest: {
          filterParams: [],
          sortParams: [],
          pageNumber: 1,
          pageSize: 20,
          quickListName: "REFERRALS_RECEIVED"
        }
      })
      dispatch({ type: actions.SET_EXPORT_DATA, payload: res?.data })
      trackPendoEvent(TRACK_EVENTS.REFERRAL_RECEIVED_EXPORT, {
        route: location?.pathname || window?.location?.pathname || "/",
        source_module: "NewRR.context",
        quicklist_type: "NEW_REFERRALS_RECEIVED",
        row_scope: parsedReferrals?.length ? "selected" : "all"
      });
    },
    clearExportData: () => {
      dispatch({ type: actions.SET_EXPORT_DATA, payload: [] })
    },
    sendEvent
  }

  const value = {
    ...getters,
    ...toggles,
    ...callbacks
  }

  return (
    <NewRRContext.Provider value={value}>
      {children}
    </NewRRContext.Provider>
  )
};
const useNewRRContext = () => useContext(NewRRContext);

export {
  NewRRProvider,
  useNewRRContext,
}
